import { zodResolver } from '@hookform/resolvers/zod';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useProducts } from '../../../data-access/products/ProductsProvider';
import {
  Category,
  ProductType,
} from '../../../data-access/products/products.models';
import {
  ProductsFilterFormInputs,
  ProductsFilterFormSchema,
} from '../components/products-query-filters/ProductsFilterForm';
import {
  ProductsSortFormInputs,
  ProductsSortFormSchema,
} from '../components/products-query-filters/ProductsSortForm';

type FormInputs = ProductsFilterFormInputs & ProductsSortFormInputs;

interface ProductsQueryContextValue {
  searchTerm: string;
  onSubmit: () => void;
  onCategoryClick: (category: Category) => void;
  onBreadcrumbBackClick: (parentCategoryId?: string) => void;
  onShowAllProducts: () => void;
}

const ProductsQueryContext = createContext<
  ProductsQueryContextValue | undefined
>(undefined);

export const ProductsQueryProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { productsFilter, fetchProducts, updateProductsFilter } = useProducts();

  const productsQueryForm = useForm<FormInputs>({
    defaultValues: {
      sortOrder: productsFilter.sortOrder,
      matches: productsFilter.matches,
      pricebookTypes: productsFilter.pricebookTypes,
      businessUnitIds: productsFilter.businessUnitIds,
      searchTerm: productsFilter.names.join(' '),
    },
    resolver: zodResolver(
      ProductsFilterFormSchema.merge(ProductsSortFormSchema)
    ),
  });

  const searchTerm = productsQueryForm.watch('searchTerm');
  const sortOrder = productsQueryForm.watch('sortOrder');

  useEffect(() => {
    updateProductsFilter({ sortOrder });
  }, [sortOrder, updateProductsFilter]);

  const queryProducts = (formValues: FormInputs, categoryIds: string[]) =>
    updateProductsFilter({
      names: formValues.searchTerm
        .split('"')
        .map((d) => d.trim())
        .filter(Boolean),
      matches: formValues.matches,
      pricebookTypes: formValues.pricebookTypes as ProductType[],
      businessUnitIds: formValues.businessUnitIds,
      categoryIds: categoryIds,
      sortOrder: formValues.sortOrder,
    });

  const onCategoryClick = (category: Category) => {
    productsQueryForm.resetField('searchTerm');
    queryProducts(productsQueryForm.getValues(), [category.categoryId]);
  };

  const onBreadcrumbBackClick = (parentCategoryId?: string) => {
    productsQueryForm.resetField('searchTerm');
    queryProducts(
      productsQueryForm.getValues(),
      parentCategoryId ? [parentCategoryId] : []
    );
  };

  const onShowAllProducts = () => {
    const filtersReset = {
      sortOrder: productsFilter.sortOrder || 'desc',
      matches: productsFilter.matches || [],
      pricebookTypes: productsFilter.pricebookTypes || [],
      businessUnitIds: productsFilter.businessUnitIds || [],
      categoryIds: [],
    };

    fetchProducts({ ...filtersReset, names: [] }).then(() =>
      productsQueryForm.reset({ ...filtersReset, searchTerm: '' })
    );
  };

  const onSubmit = productsQueryForm.handleSubmit((formValues) =>
    queryProducts(formValues, productsFilter.categoryIds)
  );

  return (
    <ProductsQueryContext.Provider
      value={{
        searchTerm,
        onSubmit,
        onCategoryClick,
        onBreadcrumbBackClick,
        onShowAllProducts,
      }}
    >
      <FormProvider {...productsQueryForm}>{children}</FormProvider>
    </ProductsQueryContext.Provider>
  );
};

export const useProductsQuery = () => {
  const context = useContext(ProductsQueryContext);
  if (context === undefined) {
    throw new Error(
      'useProductsQuery must be used within a ProductsQueryProvider'
    );
  }
  return context;
};
